// Point selection of an edge: pending edits on top of the discovered objects,
// filtering, change counts and the `PUT /edges/:id/config` body. Pure, so the
// page stays thin and the logic is unit-tested in Node.
import { ApiError } from '../api/client.js';
import type {
  AcquisitionMode,
  ConfigBody,
  DiscoveredDevice,
  EdgeObject,
  LiveValue,
} from '../api/types.js';
import {
  formatCount,
  formatFloat,
  formatLiveValue,
  formatTimestamp,
  formatTypedValue,
  PLACEHOLDER,
  qualityOf,
  type Quality,
} from './format.js';
import { unitSymbol } from './units.js';

/** Interval bounds of the config bundle (`interval_ms`, 1 s … 1 day). */
export const MIN_INTERVAL_MS = 1000;
export const MAX_INTERVAL_MS = 86_400_000;

export const ACQUISITION_MODES: readonly AcquisitionMode[] = ['cov', 'poll', 'cov+poll'];

/** BACnet object types in display order (§6 datatypes). */
export const OBJECT_TYPES = ['AI', 'AO', 'AV', 'BI', 'BO', 'BV', 'MI', 'MO', 'MV'] as const;

const DATA_TYPE: Record<string, string> = {
  AI: 'Float',
  AO: 'Float',
  AV: 'Float',
  BI: 'Boolean',
  BO: 'Boolean',
  BV: 'Boolean',
  MI: 'UInt32',
  MO: 'UInt32',
  MV: 'UInt32',
};

export interface Settings {
  selected: boolean;
  mode: AcquisitionMode;
  interval_ms: number | null;
}

/** Pending edits by `objectKey`; objects without an entry keep their server state. */
export type Edits = ReadonlyMap<string, Settings>;
export const NO_EDITS: Edits = new Map();

export function objectKey(o: Pick<EdgeObject, 'device_id' | 'metric'>): string {
  return `${o.device_id}|${o.metric}`;
}

/** `poll` and `cov+poll` use the interval; `cov` ignores it. */
export function usesInterval(mode: AcquisitionMode): boolean {
  return mode !== 'cov';
}

export function originalOf(o: EdgeObject): Settings {
  return { selected: o.selected, mode: o.mode, interval_ms: o.interval_ms };
}

export function stateOf(o: EdgeObject, edits: Edits): Settings {
  return edits.get(objectKey(o)) ?? originalOf(o);
}

export type Change = 'added' | 'removed' | 'modified';

/** What sending the selection would change for this object (null: nothing). */
export function changeOf(o: EdgeObject, edits: Edits): Change | null {
  const s = edits.get(objectKey(o));
  if (!s) return null;
  if (s.selected !== o.selected) return s.selected ? 'added' : 'removed';
  if (!s.selected) return null;
  if (s.mode !== o.mode) return 'modified';
  if (usesInterval(s.mode) && s.interval_ms !== o.interval_ms) return 'modified';
  return null;
}

function sameSettings(a: Settings, b: Settings): boolean {
  return a.selected === b.selected && a.mode === b.mode && a.interval_ms === b.interval_ms;
}

/**
 * Edits with `change` applied to `objects`. Entries that end up equal to the
 * server state are dropped; the same map is returned when nothing changed.
 */
export function applyEdit(
  edits: Edits,
  objects: readonly EdgeObject[],
  change: (s: Settings, o: EdgeObject) => Settings,
): Edits {
  let next: Map<string, Settings> | null = null;
  for (const o of objects) {
    const key = objectKey(o);
    const before = edits.get(key);
    const after = change(before ?? originalOf(o), o);
    const target = sameSettings(after, originalOf(o)) ? undefined : after;
    if (target === before || (target && before && sameSettings(target, before))) continue;
    next ??= new Map(edits);
    if (target) next.set(key, target);
    else next.delete(key);
  }
  return next ?? edits;
}

export function setSelected(edits: Edits, objects: readonly EdgeObject[], selected: boolean) {
  return applyEdit(edits, objects, (s) => ({ ...s, selected }));
}

/** Selection state of `objects` (e.g. the rows on screen) for a header checkbox. */
export function selectionOf(objects: readonly EdgeObject[], edits: Edits): 'none' | 'some' | 'all' {
  let n = 0;
  for (const o of objects) if (stateOf(o, edits).selected) n++;
  if (n === 0) return 'none';
  return n === objects.length ? 'all' : 'some';
}

/** Sets mode and/or interval of the selected ones among `objects`. */
export function setSettings(
  edits: Edits,
  objects: readonly EdgeObject[],
  patch: Partial<Pick<Settings, 'mode' | 'interval_ms'>>,
): Edits {
  return applyEdit(edits, objects, (s) => (s.selected ? { ...s, ...patch } : s));
}

export type SelectionShow = 'all' | 'selected' | 'unselected';

export interface ObjectFilter {
  /** `device_id`; empty for all devices. */
  device?: string;
  /** Object types to show; empty for all. */
  types?: readonly string[];
  /** Case-insensitive text in object name, description or metric. */
  q?: string;
  show?: SelectionShow;
  changedOnly?: boolean;
  /** Tag filter: `key=value` or `key` (any value); only points carry tags. */
  tag?: string;
}

/**
 * Search text → field test. Plain text matches anywhere in a field; with `*`
 * (any characters) or `?` (one character) the pattern must match the whole
 * field, e.g. `*CumVlm` = ends with CumVlm, `BE'H*TR` = starts and ends so.
 * `q` is lower-cased; fields are compared lower-cased.
 */
export function textMatcher(q: string): (field: string) => boolean {
  if (!/[*?]/.test(q)) return (field) => field.includes(q);
  const source = q
    .split('')
    .map((c) => (c === '*' ? '.*' : c === '?' ? '.' : c.replace(/[.+^${}()|[\]\\]/g, '\\$&')))
    .join('');
  const re = new RegExp(`^${source}$`, 's');
  return (field) => re.test(field);
}

export function filterObjects(
  objects: readonly EdgeObject[],
  edits: Edits,
  f: ObjectFilter,
): EdgeObject[] {
  const q = f.q?.trim().toLowerCase() ?? '';
  const matches = textMatcher(q);
  const types = f.types && f.types.length > 0 ? new Set(f.types) : null;
  const show = f.show ?? 'all';
  const tag = f.tag?.trim() ? parseTagFilter(f.tag) : undefined;
  return objects.filter((o) => {
    if (tag !== undefined && (tag === null || !hasTag(o.tags ?? [], tag))) return false;
    if (f.device && o.device_id !== f.device) return false;
    if (types && !types.has(o.object_type)) return false;
    if (show !== 'all' && stateOf(o, edits).selected !== (show === 'selected')) return false;
    if (f.changedOnly && !changeOf(o, edits)) return false;
    if (
      q &&
      ![
        o.object_name,
        o.description,
        o.metric,
        o.siemens_3121_path?.join(' '),
        o.siemens_4397_path?.join(' '),
      ].some((s) => (s ? matches(s.toLowerCase()) : false))
    ) {
      return false;
    }
    return true;
  });
}

export interface SelectionSummary {
  /** Selected with the pending edits. */
  selected: number;
  /** Selected on the server. */
  was: number;
  added: number;
  removed: number;
  modified: number;
}

export function summarize(objects: readonly EdgeObject[], edits: Edits): SelectionSummary {
  const sum: SelectionSummary = { selected: 0, was: 0, added: 0, removed: 0, modified: 0 };
  for (const o of objects) {
    if (o.selected) sum.was++;
    if (stateOf(o, edits).selected) sum.selected++;
    const c = changeOf(o, edits);
    if (c) sum[c]++;
  }
  return sum;
}

/**
 * Same rule as central-api: stopping all points, or at least 50 and at
 * least a fifth of them, needs an explicit confirmation.
 */
export function needsRemovalConfirm(sum: SelectionSummary): boolean {
  if (sum.removed === 0) return false;
  return sum.selected === 0 || sum.removed >= Math.max(50, sum.was * 0.2);
}

export function hasChanges(sum: SelectionSummary): boolean {
  return sum.added + sum.removed + sum.modified > 0;
}

/**
 * The full selection for `PUT /edges/:id/config`; `cov` points carry no
 * interval. `baseVersion` (the `config_version` the list was loaded with)
 * lets the server refuse the save when someone else changed the selection.
 */
export function buildConfigBody(
  objects: readonly EdgeObject[],
  edits: Edits,
  baseVersion?: number | null,
): ConfigBody {
  const points: ConfigBody['points'] = [];
  for (const o of objects) {
    const s = stateOf(o, edits);
    if (!s.selected) continue;
    points.push({
      device_id: o.device_id,
      metric: o.metric,
      mode: s.mode,
      interval_ms: usesInterval(s.mode) ? s.interval_ms : null,
    });
  }
  return baseVersion === undefined ? { points } : { points, base_version: baseVersion };
}

/** The save was refused because another selection was saved meanwhile (409). */
export function isStaleSelection(err: unknown): err is ApiError {
  return err instanceof ApiError && err.status === 409 && err.code === 'stale_selection';
}

/** What the page says after a stale save; it reloads and keeps the pending edits. */
export function staleSelectionMessage(err: ApiError, pending: number): string {
  const v = (err.body as { current_version?: unknown } | undefined)?.current_version;
  const version = typeof v === 'number' ? ` (now version ${String(v)})` : '';
  return (
    `Someone else changed the point selection of this edge while you were editing${version}. ` +
    `The list was reloaded with their changes; your ${formatCount(pending)} pending ` +
    `change${pending === 1 ? ' is' : 's are'} kept on top. Review them and save again.`
  );
}

export type IntervalInput = { ok: true; ms: number | null } | { ok: false; error: string };

/** Seconds as typed; empty means the driver default (null). */
export function parseIntervalSeconds(text: string): IntervalInput {
  const t = text.trim().replace(',', '.');
  if (!t) return { ok: true, ms: null };
  const s = Number(t);
  const ms = Math.round(s * 1000);
  if (!Number.isFinite(s) || ms < MIN_INTERVAL_MS || ms > MAX_INTERVAL_MS) {
    return { ok: false, error: 'Interval must be 1 … 86400 s' };
  }
  return { ok: true, ms };
}

export function formatIntervalSeconds(ms: number | null): string {
  return ms === null ? '' : String(ms / 1000);
}

/** The discovered present value with its unit: "21.37 °C", "On", "3". */
export function formatPresentValue(o: Pick<EdgeObject, 'object_type' | 'present_value' | 'units'>) {
  const v = o.present_value;
  if (v === null) return PLACEHOLDER;
  const dataType = DATA_TYPE[o.object_type] ?? 'Float';
  const text = formatTypedValue(dataType, typeof v === 'boolean' ? Number(v) : v);
  const unit = unitSymbol(o.units);
  return dataType === 'Boolean' || !unit ? text : `${text} ${unit}`;
}

/** Object types present in `objects`, in display order (unknown ones last). */
export function typesOf(objects: readonly EdgeObject[]): string[] {
  const present = new Set(objects.map((o) => o.object_type));
  const known: readonly string[] = OBJECT_TYPES;
  return [
    ...known.filter((t) => present.has(t)),
    ...[...present].filter((t) => !known.includes(t)).sort(),
  ];
}

// --- tags --------------------------------------------------------------------------

/** Same rules as central-api: a lowercase letter, then letters, digits, `_` or `-`. */
export const TAG_KEY = /^[a-z][a-zA-Z0-9_-]{0,63}$/;
export const TAG_VALUE_MAX = 200;
// eslint-disable-next-line no-control-regex -- control characters are what this rejects
const CONTROL = /[\u0000-\u001f\u007f]/;

export interface Tag {
  key: string;
  value: string;
}

/** `key=value` (spaces around key and value trimmed); null when invalid. */
export function parseTag(text: string): Tag | null {
  const i = text.indexOf('=');
  if (i < 0) return null;
  const key = text.slice(0, i).trim();
  const value = text.slice(i + 1).trim();
  if (!TAG_KEY.test(key) || !value || value.length > TAG_VALUE_MAX || CONTROL.test(value)) {
    return null;
  }
  return { key, value };
}

/** A filter: `key=value`, or `key` for any value; null when invalid. */
export function parseTagFilter(text: string): { key: string; value: string | null } | null {
  const t = text.trim();
  if (!t.includes('=')) return TAG_KEY.test(t) ? { key: t, value: null } : null;
  return parseTag(t);
}

export function hasTag(tags: readonly string[], f: { key: string; value: string | null }): boolean {
  return tags.some((t) => {
    const p = parseTag(t);
    return p !== null && p.key === f.key && (f.value === null || p.value === f.value);
  });
}

export interface TagListInput {
  /** Normalized `key=value` (or keys for removals), in input order. */
  tags: string[];
  errors: string[];
}

const TAG_HELP = 'key=value; key: a lowercase letter, then letters, digits, _ or -';

/**
 * Tags typed as a list, separated by commas or line breaks. One value per
 * key: a key given twice is an error.
 */
export function parseTagList(text: string): TagListInput {
  const out: TagListInput = { tags: [], errors: [] };
  const keys = new Set<string>();
  for (const part of text.split(/[,\n]/)) {
    const raw = part.trim();
    if (!raw) continue;
    const t = parseTag(raw);
    if (!t) {
      out.errors.push(`"${raw}" is not a tag (${TAG_HELP})`);
      continue;
    }
    if (keys.has(t.key)) {
      out.errors.push(`${t.key} given twice (one value per key)`);
      continue;
    }
    keys.add(t.key);
    out.tags.push(`${t.key}=${t.value}`);
  }
  return out;
}

/** Keys or tags to remove, separated by commas or line breaks. */
export function parseTagRemovals(text: string): TagListInput {
  const out: TagListInput = { tags: [], errors: [] };
  for (const part of text.split(/[,\n]/)) {
    const raw = part.trim();
    if (!raw) continue;
    const f = parseTagFilter(raw);
    if (!f) out.errors.push(`"${raw}" is not a key or key=value`);
    else out.tags.push(f.value === null ? f.key : `${f.key}=${f.value}`);
  }
  return out;
}

/** Tags of `objects` with how many carry each, sorted by tag. */
export function tagsIn(objects: readonly Pick<EdgeObject, 'tags'>[]): [string, number][] {
  const counts = new Map<string, number>();
  for (const o of objects) for (const t of o.tags ?? []) counts.set(t, (counts.get(t) ?? 0) + 1);
  return [...counts].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
}

// --- limits ------------------------------------------------------------------------

/** Object types that may carry limits (analog, §9.4). */
export const ANALOG_TYPES: readonly string[] = ['AI', 'AO', 'AV'];

export function isAnalogType(objectType: string | null | undefined): boolean {
  return objectType !== null && objectType !== undefined && ANALOG_TYPES.includes(objectType);
}

export type LimitInput = { ok: true; value: number | null } | { ok: false; error: string };

/** A limit as typed; empty = none. Decimal comma accepted. */
export function parseLimit(text: string): LimitInput {
  const t = text.trim().replace(',', '.');
  if (!t) return { ok: true, value: null };
  const v = Number(t);
  return Number.isFinite(v) ? { ok: true, value: v } : { ok: false, error: 'Not a number' };
}

/** An error text when `min > max`, else null. */
export function limitsError(min: number | null, max: number | null): string | null {
  return min !== null && max !== null && min > max ? 'Min must not be greater than max' : null;
}

/** "15 … 28 °C", "≥ 15 °C", "≤ 28 °C" or "" without limits. */
export function formatLimits(
  min: number | null | undefined,
  max: number | null | undefined,
  units?: string | null,
): string {
  const unit = unitSymbol(units ?? '');
  const u = unit ? ` ${unit}` : '';
  const lo = min ?? null;
  const hi = max ?? null;
  if (lo !== null && hi !== null) return `${formatFloat(lo)} … ${formatFloat(hi)}${u}`;
  if (lo !== null) return `≥ ${formatFloat(lo)}${u}`;
  if (hi !== null) return `≤ ${formatFloat(hi)}${u}`;
  return '';
}

// --- Devices -----------------------------------------------------------------------

/** "10.13.0.12" from "10.13.0.12:47808"; other ports are kept, null stays empty. */
export function deviceHost(address: string | null): string {
  if (!address) return '';
  return address.endsWith(':47808') ? address.slice(0, -':47808'.length) : address;
}

/** "HE'EDT1 · PXC001-E.D · #2098177 · 10.13.0.12" for device pickers. */
export function deviceOptionLabel(
  d: Pick<DiscoveredDevice, 'device_id' | 'name' | 'model' | 'instance' | 'address'>,
): string {
  const parts = [d.name ?? d.device_id];
  if (d.model) parts.push(d.model);
  parts.push(`#${String(d.instance)}`);
  const host = deviceHost(d.address);
  if (host) parts.push(host);
  return parts.join(' · ');
}

// --- COV ---------------------------------------------------------------------------

/** "COV supported · 3 active subscriptions" from the discovery of a device. */
export function formatCovSupport(
  d: Pick<
    DiscoveredDevice,
    'subscribe_cov' | 'subscribe_cov_property' | 'active_cov_subscriptions'
  >,
): string {
  const cov = d.subscribe_cov ?? null;
  const prop = d.subscribe_cov_property ?? null;
  let support: string;
  if (cov === null) support = 'COV support unknown';
  else if (cov) support = prop ? 'COV supported (also per property)' : 'COV supported';
  else support = prop ? 'COV per property only' : 'COV not supported';
  const n = d.active_cov_subscriptions ?? null;
  if (n === null) return support;
  return `${support} · ${formatCount(n)} active subscription${n === 1 ? '' : 's'}`;
}

/** The edge driver's COV states: "Kalvio: 120 subscribed, 2 rejected (polled)"; null when not reported. */
export function formatCovLive(d: Pick<DiscoveredDevice, 'cov_live'>): string | null {
  const c = d.cov_live;
  if (!c) return null;
  const parts = [`${formatCount(c.active)} subscribed`];
  if (c.pending) parts.push(`${formatCount(c.pending)} pending`);
  if (c.rejected) parts.push(`${formatCount(c.rejected)} rejected (polled)`);
  if (c.limit) parts.push(`${formatCount(c.limit)} over the limit (polled)`);
  if (c.failed) parts.push(`${formatCount(c.failed)} failed`);
  return `Kalvio: ${parts.join(', ')}`;
}

// --- values ------------------------------------------------------------------------

export interface ValueCell {
  /** `live`: the point's current value; `scan`: the value read by discovery. */
  source: 'live' | 'scan';
  text: string;
  /** Quality of a live value. */
  quality: Quality | null;
  /** Time of the live sample or of the scan. */
  ts: number | null;
  title: string;
}

/**
 * The Value column: transferred objects (with a point) show the live value,
 * the others the discovery snapshot, marked as such.
 */
export function valueCell(
  o: Pick<EdgeObject, 'object_type' | 'present_value' | 'units' | 'point_id'>,
  live: LiveValue | null | undefined,
  scannedAt: number | null | undefined,
): ValueCell {
  if (o.point_id) {
    if (!live) {
      return {
        source: 'live',
        text: PLACEHOLDER,
        quality: null,
        ts: null,
        title: 'Transferred point without a value yet',
      };
    }
    const quality = qualityOf(live.q);
    const dataType = DATA_TYPE[o.object_type] ?? 'Float';
    return {
      source: 'live',
      text: formatLiveValue(dataType, unitSymbol(o.units), live),
      quality,
      ts: live.ts,
      title: `Live value, quality ${quality}, ${formatTimestamp(live.ts)}`,
    };
  }
  const at = scannedAt ?? null;
  return {
    source: 'scan',
    text: formatPresentValue(o),
    quality: null,
    ts: at,
    title:
      at === null
        ? 'Value read by the discovery scan (not transferred)'
        : `Value read by the discovery scan on ${formatTimestamp(at)} (not transferred)`,
  };
}

/** Point ids among `rows` (the transferred ones), for the live values of one page. */
export function pointIdsOf(rows: readonly Pick<EdgeObject, 'point_id'>[]): string[] {
  return rows.flatMap((o) => (o.point_id ? [o.point_id] : []));
}
